import cn from 'classnames';
import { Formik, FormikProps } from 'formik';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  BASE_URL,
  COPYRIGHT,
  Instance,
  apiUrlCookieName,
  SECONDS_IN_HOUR,
} from '@const/consts';
import { RouteName } from '@const/paths';

import { useAppSelector } from '@hooks/redux';

import { getBaseUrl, setFormLanguage } from '@utils/utils';

import LanguageSelectorWrapper from '@common/LanguageSelectorWrapper';
import {
  AuthLoader,
  AuthLayoutNew,
  Alert,
  useStateWithCallback,
} from '@infologistics/frontend-libraries';

import logo from '../../img/logo.svg';
import styles from '../../Auth.module.scss';

import cookies from '@app/utils/cookies';
import BaseUrlForm from '../Signin/BaseUrlForm';
import { IBaseUrlFormState } from '../Signin/types';

export type ILoginLayoutContext = {
  isSending: boolean;
  setSending: (
    updatedState: React.SetStateAction<boolean>,
    callback?: ((updatedState: boolean) => void) | undefined,
  ) => void;
  serverError: boolean;
  setServerError: (message: string) => void;
};

const LoginLayout: FC = () => {
  const [baseUrl, setBaseUrl] = useState('');
  const [isBoldText, setIsBoldText] = useState(false);

  const initialBaseUrlValues = { baseUrl, isBoldText: false };

  useEffect(() => {
    const baseUrl = getBaseUrl();

    setBaseUrl(baseUrl);

    if (!sessionStorage.getItem(apiUrlCookieName)) {
      sessionStorage.setItem(apiUrlCookieName, baseUrl);
    }
  }, []);

  const { t } = useTranslation(['auth', 'common']);

  const { isAuthenticated } = useAppSelector((state) => state.user);
  const { instance } = useAppSelector((state) => state.utils);

  const copyright = instance !== Instance.RU ? COPYRIGHT : undefined;
  const [isSending, setSending] = useStateWithCallback(false);
  const [serverError, setServerError] = useState('');

  const navigate = useNavigate();
  const locationPathname = useLocation().pathname;

  const title =
    locationPathname === RouteName.PASSWORD_RESET ||
    locationPathname === RouteName.PASSWORD_RESET_CONFIRM
      ? t('auth:passwordReset')
      : t('auth:authorization');

  useEffect(() => {
    if (isAuthenticated) {
      navigate(RouteName.DEFAULT);
    }

    setFormLanguage();
  }, []);

  const setBaseUrlParams = (isBoldText = false, baseUrl?: string): void => {
    setIsBoldText(isBoldText);

    if (baseUrl) {
      setBaseUrl(baseUrl);

      cookies.set(apiUrlCookieName, baseUrl, {
        maxAge: SECONDS_IN_HOUR * 24,
        path: '/',
      });
    }
  };

  const handleSubmitBaseUrl = ({ baseUrl }: IBaseUrlFormState): void =>
    setBaseUrlParams(true, baseUrl);
  const handleResetFormBaseUrl = (): void => setBaseUrlParams(false, BASE_URL);

  const renderFormBaseUrl = (
    props: FormikProps<IBaseUrlFormState>,
  ): ReactElement => {
    return (
      <BaseUrlForm
        {...props}
        baseUrl={baseUrl}
        isBoldText={isBoldText}
        resetText={setBaseUrlParams}
        resetForm={handleResetFormBaseUrl}
      />
    );
  };

  return (
    <AuthLayoutNew
      classes={styles.layout}
      logo={logo}
      logoAlt='greenDocs'
      description={t('auth:description')}
      copyright={copyright}
    >
      <div className={cn(styles.container, 'relative')}>
        <div className='mb-2 d-flex justify-content-between'>
          <h1 className={cn(styles.title_page, 'mb-0 fw-400')}>{title}</h1>
          <LanguageSelectorWrapper isThemeMini />
        </div>
        <div className={cn(styles.alert_wrapper, 'mb-2')}>
          {serverError && (
            <Alert
              classes={cn(styles.alert, styles.alert_danger)}
              title={serverError}
              theme='danger'
            />
          )}
        </div>

        <Outlet
          context={{
            isSending: isSending,
            setSending: setSending,
            serverError: serverError,
            setServerError: setServerError,
          }}
        />
      </div>

      {process.env.REACT_APP_API_ENV === 'development' ? (
        <Formik
          initialValues={initialBaseUrlValues}
          onSubmit={handleSubmitBaseUrl}
          enableReinitialize
        >
          {renderFormBaseUrl}
        </Formik>
      ) : null}

      {isSending ? <AuthLoader /> : null}
    </AuthLayoutNew>
  );
};

export default LoginLayout;
